import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_CONFIG } from "../../Config/config-env.js";
import { applyExternalDarkMode } from "../../Config/functions.js";
import LockOverlay from "../../Components/LockOverlay";

/*
  Formulario base de tareas de pedido para tenants ElevaX (Auren y similares).

  Se carga dentro del iframe de la tarea de xFlow (campo type:"iframe", EXTERNAL) y
  recibe por querystring el mismo contrato que los formularios de CASA_IDEAS_V2:
    wfpflowid, wfptaskid, officecode, token, options (opciones de la tarea, separadas
    por coma), locked=true (tarea tomada por otro usuario), popup, darkmode.

  Muestra los datos del pedido (variables del flujo), una guia de que hacer en este
  paso, y un boton por cada opcion de la tarea (cada opcion es una transicion del
  flujo). Al ejecutar avisa al padre con postMessage("TAREA_COMPLETADA").

  Cada pantalla concreta (externalForm01..05) solo pasa una `config`:
    stage        indice del paso en la tira de progreso (0 = preparar, 1 = entrega)
    title        titulo de la tarea
    intro        una frase de que se espera en este paso
    checklist    ayudas cortas (no bloquean nada)
    sections     que bloques de datos del pedido mostrar: "pedido" | "cliente" | "entrega"
    optionHints  { "<nombre de la opcion>": "a donde lleva" }
    stages       etiquetas de la tira de progreso (por defecto Preparación/Entrega/Cierre)
    inputs       campos editables [{name,label,type,required,placeholder}] que viajan como params
    inputsTitle  titulo del bloque de campos
    skipRequiredFor  opciones que no exigen los campos requeridos (ej. cancelar)
    customSections  [{title, fields:[[var, etiqueta]]}] se usan con sections:["custom"]
    noteLabel    si existe, muestra una nota opcional que viaja como variable `observacion`
*/

const STAGES = ["Preparación", "Entrega", "Cierre"];

const SECTION_FIELDS = {
  pedido: [
    ["order_id", "N.º de pedido"],
    ["date_added", "Fecha del pedido"],
    ["storename", "Tienda"],
    ["tipo_envio_etiqueta", "Tipo de envío"],
    ["storepickupname", "Tienda de retiro"],
    ["metodo_pago", "Método de pago"],
    ["comment", "Comentario del cliente"],
  ],
  montos: [
    ["subtotal", "Subtotal"],
    ["discount", "Descuento"],
    ["cupon_cliente", "Cupón"],
    ["shipping_cost", "Envío"],
    ["monto_total", "Total a pagar"],
  ],
  facturacion: [
    ["tipo_documento", "Tipo de documento"],
    ["nit", "NIT / documento"],
    ["razon_social", "Razón social"],
  ],
  cliente: [
    ["nombre_cliente", "Cliente"],
    ["telefono", "Teléfono"],
    ["email", "Email"],
  ],
  entrega: [
    ["direccion_1", "Dirección"],
    ["direccion_2", "Referencia"],
    ["ciudad", "Ciudad"],
    ["zona", "Zona"],
  ],
};
const SECTION_TITLES = {
  pedido: "Pedido",
  montos: "Montos",
  facturacion: "Facturación",
  cliente: "Cliente",
  entrega: "Dirección de entrega",
};
const MONEY = ["subtotal", "discount", "shipping_cost", "monto_total"];

// `productos` viaja como JSON en una variable del flujo
function parseProductos(raw) {
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

// "20261016 20:11:45" -> "16/10/2026 20:11"
function formatValue(name, value) {
  const v = String(value == null ? "" : value).trim();
  if (!v || v === "-" || v === "null" || v === "undefined") return "";
  if (name === "date_added") {
    const m = v.match(/^(\d{4})(\d{2})(\d{2}) (\d{2}):(\d{2})/);
    if (m) return m[3] + "/" + m[2] + "/" + m[1] + " " + m[4] + ":" + m[5];
  }
  if (MONEY.includes(name) && !isNaN(Number(v))) return Number(v).toFixed(2);
  return v;
}

function optionTone(name) {
  const n = String(name || "").toLowerCase();
  if (/cancel/.test(n)) return "danger";
  if (/falta|observ|no entreg|no retir/.test(n)) return "warn";
  return "ok";
}

const CSS = `
.oxf{--bg:#f6f7f9;--card:#fff;--ink:#1c2430;--mut:#667085;--line:#e4e7ec;--ok:#0f7b4d;--warn:#b45309;--danger:#b42318;--acc:#2b5fd9;
  font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:var(--ink);background:var(--bg);padding:12px;min-height:100vh;box-sizing:border-box}
.xflow-external-dark .oxf{--bg:#111827;--card:#1b2433;--ink:#e6eaf2;--mut:#9aa6b8;--line:#2c3a50;--acc:#7aa2ff}
.oxf *{box-sizing:border-box}
.oxf h2{font-size:18px;margin:0 0 4px;font-weight:650}
.oxf .intro{color:var(--mut);margin:0 0 12px;font-size:14px}
.oxf .stages{display:flex;gap:6px;margin:0 0 14px}
.oxf .stage{flex:1;text-align:center;font-size:12px;padding:6px 4px;border-radius:999px;background:var(--card);border:1px solid var(--line);color:var(--mut)}
.oxf .stage.on{background:var(--acc);border-color:var(--acc);color:#fff;font-weight:600}
.oxf .stage.done{color:var(--ok);border-color:var(--ok)}
.oxf .card{background:var(--card);border:1px solid var(--line);border-radius:12px;padding:12px 14px;margin-bottom:10px}
.oxf .card h3{font-size:12px;letter-spacing:.04em;text-transform:uppercase;color:var(--mut);margin:0 0 8px;font-weight:600}
.oxf dl{margin:0;display:grid;grid-template-columns:minmax(110px,32%) 1fr;gap:6px 12px;font-size:14px}
.oxf dt{color:var(--mut)} .oxf dd{margin:0;font-weight:500;word-break:break-word}
.oxf a{color:var(--acc)}
@keyframes oxspin{to{transform:rotate(360deg)}}
.oxf .spin{display:inline-block;width:14px;height:14px;margin-right:8px;vertical-align:-2px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:oxspin .7s linear infinite}
.oxf button.opt.busy{opacity:1}
.oxf ul.prods{list-style:none;margin:0;padding:0}
.oxf ul.prods li{display:flex;justify-content:space-between;gap:10px;padding:8px 0;border-top:1px solid var(--line);font-size:14px}
.oxf ul.prods li:first-child{border-top:0;padding-top:0}
.oxf ul.prods small{display:block;color:var(--mut)}
.oxf ul.prods .qty{white-space:nowrap;font-weight:600}
.oxf ul.check{margin:0;padding-left:18px;font-size:14px} .oxf ul.check li{margin:3px 0}
.oxf textarea{width:100%;min-height:64px;border:1px solid var(--line);border-radius:8px;padding:8px;font:inherit;background:var(--card);color:var(--ink)}
.oxf .inputs{display:grid;gap:10px} .oxf .inputs label span{display:block;font-size:13px;color:var(--mut);margin-bottom:3px}
.oxf .inputs input{width:100%;border:1px solid var(--line);border-radius:8px;padding:9px;font:inherit;background:var(--card);color:var(--ink)}
.oxf .actions{display:grid;gap:8px;margin-top:6px}
.oxf button.opt{width:100%;text-align:left;border:1px solid var(--line);background:var(--card);color:var(--ink);border-radius:12px;padding:12px 14px;font:inherit;cursor:pointer}
.oxf button.opt b{display:block;font-size:15px}
.oxf button.opt small{display:block;color:var(--mut);margin-top:2px}
.oxf button.opt.ok{border-color:var(--ok);background:var(--ok);color:#fff} .oxf button.opt.ok small{color:#e6f4ee}
.oxf button.opt.warn{border-color:var(--warn)} .oxf button.opt.warn b{color:var(--warn)}
.oxf button.opt.danger{border-color:var(--danger)} .oxf button.opt.danger b{color:var(--danger)}
.oxf button.opt:disabled{opacity:.55;cursor:default}
.oxf .msg{padding:10px 12px;border-radius:10px;font-size:14px;margin-bottom:10px}
.oxf .msg.err{background:#fef3f2;color:var(--danger);border:1px solid #fecdca}
.oxf .msg.okm{background:#ecfdf3;color:var(--ok);border:1px solid #abefc6}
.oxf .muted{color:var(--mut);font-size:13px}
`;

export default function OrderTaskForm(props) {
  const config = props.config || {};
  const search = (props.location && props.location.search) || window.location.search;
  const query = useMemo(() => new URLSearchParams(search), [search]);

  const token = query.get("token");
  const wfpflowid = query.get("wfpflowid");
  const wfptaskid = String(query.get("wfptaskid") || "").split("___")[0];
  const locked = query.get("locked") === "true";
  const popup = query.get("popup") === "true";
  const options = (query.get("options") || "").split(",").map((o) => o.trim()).filter(Boolean);

  const [vars, setVars] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [executing, setExecuting] = useState(false);
  const [done, setDone] = useState("");
  const [pending, setPending] = useState("");
  const [note, setNote] = useState("");
  const [inputVals, setInputVals] = useState({});

  useEffect(() => {
    applyExternalDarkMode(search);
    let alive = true;
    axios({
      method: "post",
      url: API_CONFIG.xflowurl + "/engine-users/get-task-details",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      data: { wfptaskid: wfptaskid, isParent: false },
    })
      .then((r) => r.data)
      .then((data) => {
        if (!alive) return;
        if (!data.status) {
          setError("No se pudieron cargar los datos del pedido.");
        } else {
          const map = {};
          (data.flowvars || []).forEach((v) => {
            if (v.WFPFLOWID == null || String(v.WFPFLOWID) === String(wfpflowid)) map[v.VARNAME] = v.VARVALUE;
          });
          setVars(map);
          const init = {};
          (config.inputs || []).forEach((f) => {
            if (map[f.name]) init[f.name] = map[f.name];
          });
          setInputVals(init);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError("No se pudo conectar con xFlow.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
    // token / wfpflowid / wfptaskid se derivan de `search`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const execute = (optionName) => {
    if (locked || executing) return;
    setError("");
    const params = {};
    const skip = (config.skipRequiredFor || []).includes(optionName);
    if (!skip) {
      const missing = (config.inputs || []).filter((f) => f.required && !String(inputVals[f.name] || "").trim());
      if (missing.length) {
        setError("Completa: " + missing.map((f) => f.label).join(", "));
        return;
      }
    }
    (config.inputs || []).forEach((f) => {
      const v = String(inputVals[f.name] == null ? "" : inputVals[f.name]).trim();
      if (v) params[f.name] = v;
    });
    setExecuting(true);
    setPending(optionName);
    if (config.noteLabel && note.trim()) params.observacion = note.trim();
    axios({
      method: "put",
      url: API_CONFIG.xflowurl + "/engine-users/execute-task",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      data: { parentid: wfptaskid, id: wfptaskid, isParent: false, optionValue: optionName, params: params },
    })
      .then((r) => r.data)
      .then((data) => {
        if (data.status) {
          setDone(optionName);
          setTimeout(() => {
            try {
              if (popup && window.opener) {
                window.opener.postMessage("TAREA_COMPLETADA", "*");
                window.close();
              } else {
                window.parent.postMessage("TAREA_COMPLETADA", "*");
              }
            } catch (e) {
              /* sin padre: el usuario ve el mensaje de exito */
            }
          }, 900);
        } else {
          setError("No se pudo ejecutar la tarea: " + (data.message || "error desconocido"));
          setExecuting(false);
          setPending("");
        }
      })
      .catch(() => {
        setError("No se pudo ejecutar la tarea. Intenta de nuevo.");
        setExecuting(false);
        setPending("");
      });
  };

  const stage = config.stage == null ? 0 : config.stage;
  const hints = config.optionHints || {};
  const sections = config.sections || ["pedido", "cliente"];
  const phoneDigits = String(vars.telefono || "").replace(/[^\d]/g, "");

  return (
    <div className="oxf">
      <style>{CSS}</style>
      <LockOverlay visible={locked && !loading} />

      <h2>{config.title}</h2>
      {config.intro && <p className="intro">{config.intro}</p>}

      <div className="stages">
        {(config.stages || STAGES).map((s, i) => (
          <div key={s} className={"stage " + (i === stage ? "on" : i < stage ? "done" : "")}>
            {i < stage ? "✓ " : ""}
            {s}
          </div>
        ))}
      </div>

      {loading && <p className="muted"><span className="spin" />Cargando datos del pedido…</p>}
      {error && <div className="msg err">{error}</div>}
      {pending && !done && <div className="msg okm"><span className="spin" />Procesando «{pending}»… no cierres esta ventana.</div>}
      {done && <div className="msg okm"><span className="spin" />Listo: «{done}». Actualizando la bandeja…</div>}

      {!loading &&
        sections.map((sec) => {
          if (sec === "custom") {
            return (config.customSections || []).map((cs) => {
              const rows = cs.fields.map(([n, l]) => [n, l, String(vars[n] == null ? "" : vars[n]).trim()]).filter((r) => r[2]);
              if (!rows.length) return null;
              return (
                <div className="card" key={cs.title}>
                  <h3>{cs.title}</h3>
                  <dl>
                    {rows.map(([n, l, v]) => (
                      <React.Fragment key={n}>
                        <dt>{l}</dt>
                        <dd style={{ whiteSpace: "pre-wrap" }}>{v}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              );
            });
          }
          if (sec === "productos") {
            const prods = parseProductos(vars.productos);
            if (!prods.length) return null;
            return (
              <div className="card" key={sec}>
                <h3>Productos ({prods.reduce((n, p) => n + (Number(p.cantidad) || 0), 0)})</h3>
                <ul className="prods">
                  {prods.map((p, i) => (
                    <li key={i}>
                      <span>
                        {p.nombre || p.sku}
                        <small>
                          {[p.sku, p.talla && "Talla " + p.talla, p.color].filter(Boolean).join(" · ")}
                        </small>
                      </span>
                      <span className="qty">
                        {p.cantidad} × {p.precio != null && p.precio !== "" ? Number(p.precio).toFixed(2) : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          const rows = (SECTION_FIELDS[sec] || [])
            .map(([name, label]) => [name, label, formatValue(name, vars[name])])
            .filter((r) => r[2]);
          if (!rows.length) return null;
          return (
            <div className="card" key={sec}>
              <h3>{SECTION_TITLES[sec]}</h3>
              <dl>
                {rows.map(([name, label, value]) => (
                  <React.Fragment key={name}>
                    <dt>{label}</dt>
                    <dd>
                      {name === "telefono" && phoneDigits ? (
                        <>
                          <a href={"tel:+" + phoneDigits}>{value}</a>{" · "}
                          <a href={"https://wa.me/" + phoneDigits} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                        </>
                      ) : (
                        value
                      )}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          );
        })}

      {!loading && config.inputs && config.inputs.length > 0 && (
        <div className="card">
          <h3>{config.inputsTitle || "Datos"}</h3>
          <div className="inputs">
            {config.inputs.map((f) => (
              <label key={f.name}>
                <span>{f.label}{f.required ? " *" : ""}</span>
                {f.type === "textarea" ? (
                  <textarea
                    value={inputVals[f.name] || ""}
                    placeholder={f.placeholder || ""}
                    disabled={locked}
                    onChange={(e) => setInputVals({ ...inputVals, [f.name]: e.target.value })}
                  />
                ) : (
                  <input
                    type={f.type || "text"}
                    value={inputVals[f.name] || ""}
                    placeholder={f.placeholder || ""}
                    disabled={locked}
                    onChange={(e) => setInputVals({ ...inputVals, [f.name]: e.target.value })}
                  />
                )}
              </label>
            ))}
          </div>
        </div>
      )}

      {config.checklist && config.checklist.length > 0 && (
        <div className="card">
          <h3>Qué hacer en este paso</h3>
          <ul className="check">
            {config.checklist.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {config.noteLabel && !locked && (
        <div className="card">
          <h3>{config.noteLabel}</h3>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opcional" />
        </div>
      )}

      {!loading && (
        <div className="actions">
          {options.length === 0 && !locked && <p className="muted">Esta tarea no tiene acciones disponibles.</p>}
          {options.map((o) => (
            <button
              key={o}
              type="button"
              className={"opt " + optionTone(o) + (pending === o ? " busy" : "")}
              disabled={executing || !!done || locked}
              onClick={() => execute(o)}
            >
              <b>{pending === o && <span className="spin" />}{o}</b>
              {hints[o] && <small>{hints[o]}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
